import React from "react"
import styled from "styled-components"
import { Helmet } from "react-helmet-async"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { trackEvent } from "../../../../analytics/agile"
import { pushLeadEvent } from "../../../../utils/analytics"

// Reusable components
import Popup from "@components/layout/Popup"
import ReusableHero from "@components/layout/Hero/ReusableHero"
import ReusableServicesOverview from "@components/layout/sections/ReusableServicesOverview"
import { getQuoteFormSchema, renderQuoteForm } from "@components/layout/sections/ReusableCallToAction/Quote"
import ReusableCallToAction from "@components/layout/sections/ReusableCallToAction"
import { Layout } from "../Brisson3PL"
import api from "@utils/api.client"
import { ProfessionalFeatureSection } from "./ProfessionalFeatureSection"
import { ModernStorageSection } from "./ModernStorageSection"

const ArtSolutions = () => {
  const { t, ready } = useTranslation("services/artSolutions")
  const navigate = useNavigate()

  if (!ready) return <div />

  // Retrieve translation objects
  const servicesOverviewData = t("servicesOverview", { returnObjects: true }) as any
  servicesOverviewData.cards = servicesOverviewData.cards.map((card: any) => ({
    ...card,
    logo: "/img/Logo-white-Art-Solution.svg",
  }))

  const whyChooseData = t("whyChoose", { returnObjects: true }) as any
  const storageData = t("premiumStorage", { returnObjects: true }) as any

  const initialValues = { inquiryType: "ART", name: "", email: "", phoneNumber: "", message: "" }

  // Submission handler — tracking succès
  const handleSubmit = async (values: typeof initialValues, actions: any) => {
    try {
      const response = await api.post("/inquiries/art", values)
      if (response.status === 200 || response.status === 201) {
        // 🔽 Tracking succès (Agile + dataLayer) AVANT la navigation
        trackEvent("DemandeSoumission", { page: window.location.pathname, type: "form" })
        pushLeadEvent("art_quote", { page: window.location.pathname })

        actions.resetForm()
        actions.setSubmitting(false)

        // Petit délai pour garantir l’envoi des events
        setTimeout(() => navigate(t("cta.successRedirectUrl")), 100)
      }
    } catch (error) {
      console.error("Error submitting art inquiry:", error)
      actions.setSubmitting(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>{t("meta.title")}</title>
        <meta name="description" content={t("meta.description")} />
        <meta name="keywords" content={t("meta.keywords")} />
        <meta property="og:title" content={t("meta.ogTitle")} />
        <meta property="og:description" content={t("meta.ogDescription")} />
        <meta property="og:image" content={t("meta.ogImage")} />
        <meta property="og:url" content={t("meta.ogUrl")} />
      </Helmet>

      <Layout>
        <ReusableHero
          headingPre={t("hero.headingPre")}
          headingHighlight={t("hero.headingHighlight")}
          headingPost={t("hero.headingPost")}
          subtitle={t("hero.subtitle")}
          imageSrc="/img/love-transport.png"
          imageAlt={t("hero.imageAlt")}
          ctaLabel={t("hero.ctaLabel")}
          ctaOnClick={() => {
            // 🔽 Tracking clic CTA Héro
            trackEvent("CTA_DemandeSoumission", {
              page: window.location.pathname,
              position: "hero_art",
            })
            navigate("/faire-une-demande")
          }}
        />

        <ReusableServicesOverview
          bgImg="/img/art-solution-art-transport.png"
          hasOverlay
          cardsData={servicesOverviewData.cards}
          autoRotateInterval={5000}
        />

        <ProfessionalFeatureSection data={whyChooseData} />
        <ModernStorageSection data={storageData} />

        <ReusableCallToAction
          inquiryType="ART"
          heading={t("cta.heading")}
          text={t("cta.text")}
          initialValues={initialValues}
          validationSchema={getQuoteFormSchema(t)}
          onSubmit={handleSubmit}
          renderForm={(formikProps) => renderQuoteForm(formikProps, t)}
          portraitSrc="/img/art-cta.png"
          logoMarkSrc="/img/LogoMark.svg"
          transformPortraitImg="translateY(10vh)"
        />
      </Layout>
    </>
  )
}

export default ArtSolutions
